import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, List, Mail } from "lucide-react";
import SEO from "../components/SEO";

interface ShowMoreTextProps {
  text: string;
  limit?: number;
}

const ShowMoreText: React.FC<ShowMoreTextProps> = ({ text, limit = 200 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (text.length <= limit) {
    return (
      <p className="mt-3 text-sm text-text-sub dark:text-gray-400 leading-relaxed">
        {text}
      </p>
    );
  }

  return (
    <div className="mt-3 text-sm text-text-sub dark:text-gray-400 leading-relaxed">
      <p>
        {isExpanded ? text : `${text.substring(0, limit)}...`}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className="ml-1.5 text-primary dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors font-bold text-xs uppercase tracking-wider focus:outline-none"
        >
          {isExpanded ? "Show Less" : "View More"}
        </button>
      </p>
    </div>
  );
};

const STATIC_TEAM = [
  {
    name: "Abdullahi Bala Musa",
    role: "Founder & CEO",
    intro: `With Deep Expertise in Digital Transformation, Cybersecurity, and Technology innovation, he leads InnovaTech’s strategic growth and delivers impactful solutions that empower businesses, institutions, and communities across Nigeria. Mr. Abdullahi Bala Musa holds a Bachelor’s Degree in Cybersecurity and is an alumnus of the prestigious Informatics Institute Singapore (Nigerian Campus), where he obtained an International Diploma in Information Technology followed by an International Advanced Diploma in Network & Computer Security. In September 2024, he signed a contract with the global company Cryptomus as a Business Development Manager, driving partner acquisition and B2B integrations to expand the adoption of the company’s cryptocurrency payment ecosystem. Mr. Bala also serves as an Ad-hoc Technical Support Specialist with the Community Health Practitioners Registration Board of Nigeria, providing essential digital support services to the institution. Additionally, he facilitates the Digital Economy Boot Camp at Jigawa Broadcasting Corporation, where he contributes to building digital literacy and strengthening the tech capacity of youths across the state.`,
    tags: [
      "Strategic Planning",
      "Business Dev",
      "Digital Transformation",
      "Leadership",
    ],
    img: "/teams/ceo.webp",
    socials: {
      linkedin:
        "https://www.linkedin.com/in/abdullahi-bala-musa-cc-93a3513b0?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app",
      x: "https://x.com/abdoullbmk1",
      instagram: "https://instagram.com/abdoullbmk",
      email: "mailto:abalamusa39@gmail.com",
    },
  },
  {
    name: "Yusuf Lawan Nuhu",
    role: "Chief Operating Officer (COO)",
    intro: `Yusuf specializes in building secure, scalable platforms that enhance business performance and drive digital innovation. His comprehensive background spans cybersecurity, full-stack software development, data science, and strategic product planning. As a seasoned technical leader, he masters every layer of the stack, from crafting intuitive front-end interfaces to architecting robust back-end systems. His deep expertise in Cyber Security ensures that every solution InnovaTech builds is not only high-performing but also fortified against sophisticated digital threats. By bridging the gap between complex engineering and strategic business goals, Yusuf delivers technology that empowers organizations to thrive securely.`,
    tags: ["Critical thinking", "Decision making", "Full stack developer"],
    img: "/teams/coo.webp",
    socials: {
      linkedin: "https://www.linkedin.com/in/yusuf-lawan-nuhu-79054b245",
      x: "https://x.com/cyberwarrior___",
      email: "mailto:ylawan685@gmail.com",
      github: "https://github.com/Yoosuph",
    },
  },
  {
    name: "Munir Isah",
    role: "Head of Development",
    intro: `With strong expertise in penetration testing, backend development, and DevOps, Munir plays a key role in driving our organization’s technical innovation and ensuring the delivery of secure, scalable, and high-performing solutions. Over the years, he has worked on a diverse range of mobile, desktop, and web applications, applying a security-first mindset and modern engineering practices to every stage of development. Munir holds a solid foundation in software engineering and cybersecurity, continuously refining his skills through hands-on projects and advanced technical training. His work spans secure system architecture, automation, infrastructure optimization, and application hardening—helping clients build resilient digital systems that meet industry standards. In addition to his development responsibilities, he contributes to improving internal DevOps processes, strengthens security posture across projects, and mentors teams on best practices. Munir’s commitment to excellence, efficiency, and continuous improvement ensures that every solution delivered is robust, reliable, and aligned with organizational goals`,
    tags: ["DevOps", "System Architecture", "Full stack"],
    img: "/teams/Head of Development.webp",
    socials: {
      x: "https://x.com/munier_ie",
      email: "mailto:muonier.ie@gmail.com",
      github: "https://github.com/munier-ie",
    },
  },
  {
    name: "Abdulmalik Abubakar Umar",
    role: "Business Manager",
    intro: `Abdulmalik Abubakar Umar is a strategic Business Manager at InnovaTech, dedicated to steering the organization towards sustainable growth and operational excellence. With a robust background in business strategy and client relationship management, he oversees the seamless integration of technical solutions into business workflows, ensuring maximum value for stakeholders. Abdulmalik excels at identifying market opportunities, fostering strategic partnerships, and optimizing internal resources to drive InnovaTech's mission of digital empowerment across Nigeria.`,
    tags: [
      "Strategic Partnerships",
      "Market Analytics",
      "Operational Efficiency",
    ],
    img: "/teams/Business Manager.webp",
    socials: {
      linkedin: "https://www.linkedin.com/in/abdulmalik-abubakar-a7289a3a2",
      instagram: "https://www.instagram.com/abduuuul00",
    },
  },
  {
    name: "Aliyu Usman Haruna",
    role: "Developer",
    intro: `Aliyu Usman Haruna is an innovative Developer at InnovaTech, specializing in the design and implementation of high-performance digital solutions. With proficiency across modern development stacks and a keen eye for architectural efficiency, Aliyu transforms complex requirements into scalable, secure, and user-centric applications. His dedication to clean code and agile methodologies allows him to deliver robust software that powers business success, while his proactive approach to problem-solving ensures that every challenge is met with a technical solution that is both elegant and effective.`,
    tags: ["UI/UX Implementation", "API Integration", "Frontend Engineering"],
    img: "/teams/Developer.webp",
    socials: {
      linkedin: "https://www.linkedin.com/in/aliyu-usman-16a31b312",
    },
  },
  {
    name: "Adam Muhammad Ibrahim",
    role: "Head of Business Development",
    intro: `Adam Muhammad Ibrahim brings a wealth of entrepreneurial expertise and strategic vision to InnovaTech as the Head of Business Development. With a proven track record of driving market expansion and forging high-impact partnerships, Adam is instrumental in shaping the company's growth trajectory. His deep understanding of business dynamics and innovative approach to problem-solving enable InnovaTech to navigate complex market landscapes and deliver unparalleled value to clients. Adam's leadership focuses on cultivating sustainable strategic partnerships and identifying new avenues for technological empowerment.`,
    tags: ["Business Strategy", "Market Expansion", "Strategic Partnerships"],
    img: "/teams/adam.webp",
    socials: {
      x: "https://x.com/3rdkhalifaa?s=21",
      instagram:
        "https://www.instagram.com/schlstt?igsh=Z2p6bDM0OHdheWs%3D&utm_source=qr",
    },
  },
];

const DirectoryScreen: React.FC = () => {
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const navigate = useNavigate();
  const [team, setTeam] = useState<any[]>([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(true);

  useEffect(() => {
    fetch("/api/team")
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const mapped = data.map((m: any) => ({
            ...m,
            img: m.image || m.img,
            intro: m.intro || m.bio || "",
          }));
          setTeam(mapped);
        }
      })
      .catch((err) => console.warn("Failed to load team from API:", err))
      .finally(() => setIsLoadingTeam(false));
  }, []);

  // IntersectionObserver for soft reveal entry transition
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: "0px 0px -5% 0px" }
    );

    const revealElements = document.querySelectorAll(".reveal");
    revealElements.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
    };
  }, [viewMode]);

  return (
    <div className="relative w-full overflow-hidden flex justify-center px-4 md:px-6 lg:px-8 mt-6 md:mt-12 bg-transparent">
      {/* Soft Premium Custom Styles - Highly Optimized */}
      <style>{`
        .mesh-container {
          position: fixed;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
          background: 
            radial-gradient(circle at 15% 15%, rgba(238, 44, 40, 0.08) 0%, transparent 48%),
            radial-gradient(circle at 85% 85%, rgba(12, 27, 46, 0.04) 0%, transparent 45%);
          transition: background 500ms ease;
        }
        .dark .mesh-container {
          background: 
            radial-gradient(circle at 15% 15%, rgba(238, 44, 40, 0.06) 0%, transparent 48%),
            radial-gradient(circle at 85% 85%, rgba(238, 44, 40, 0.04) 0%, transparent 45%);
        }
        .reveal {
          opacity: 0;
          transform: translateY(16px);
          filter: blur(4px);
          transition: opacity 800ms cubic-bezier(0.32, 0.72, 0, 1),
                      transform 800ms cubic-bezier(0.32, 0.72, 0, 1),
                      filter 800ms cubic-bezier(0.32, 0.72, 0, 1);
          will-change: opacity, transform, filter;
        }
        .reveal.is-in {
          opacity: 1;
          transform: translateY(0) scale(1);
          filter: blur(0);
        }
        .card-spring {
          transition: transform 700ms cubic-bezier(0.32, 0.72, 0, 1),
                      box-shadow 700ms cubic-bezier(0.32, 0.72, 0, 1),
                      border-color 500ms cubic-bezier(0.32, 0.72, 0, 1);
          will-change: transform;
        }
        @media (prefers-reduced-motion: reduce) {
          .reveal {
            opacity: 1 !important;
            transform: none !important;
            filter: none !important;
          }
        }
      `}</style>

      {/* Fixed Ambient Mesh - Fast Draw Calls */}
      <div className="mesh-container" aria-hidden="true" />

      <SEO
        title="Team | InnovaTech"
        description="Meet the specialized team behind InnovaTech. Experts in Cybersecurity, Software Engineering, and Digital Transformation in Nigeria."
        canonical="/team"
      >
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: team.map((member, index) => ({
              "@type": "ListItem",
              position: index + 1,
              item: {
                "@type": "Person",
                name: member.name,
                jobTitle: member.role,
                image: member.img?.startsWith('http')
                  ? member.img
                  : member.img && !member.img.startsWith('data:')
                  ? `https://www.innovatech-ng.com${member.img.startsWith('/') ? '' : '/'}${member.img}`
                  : undefined,
                description: member.intro,
                sameAs: Object.values(member.socials as Record<string, string>).filter(
                  (url) => !url.startsWith("mailto:"),
                ),
              },
            })),
          })}
        </script>
      </SEO>

      <div className="relative z-10 flex w-full max-w-[1100px] flex-col">
        {/* Header Block - Soft Premium */}
        <div className="reveal mb-16 flex flex-col gap-6 border-b border-black/[0.06] dark:border-white/10 pb-10">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/60 dark:bg-white/5 border border-black/5 dark:border-white/5 w-fit shadow-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"></span>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-text-sub dark:text-gray-300">
              02 · DIRECTORY
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] text-text-main dark:text-white leading-[0.98]">
            The Minds Behind <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-red-500">
              InnovaTech.
            </span>
          </h1>
          <p className="max-w-2xl text-lg font-light leading-relaxed text-text-sub dark:text-gray-400">
            Building great software and secure systems takes a team of friendly, experienced experts. Meet the wonderful people behind the work we do at InnovaTech.
          </p>
        </div>

        {/* Filters & Grid/List Toggle Switch */}
        <div className="reveal mb-10 flex items-center justify-between gap-4">
          <p className="font-mono text-xs font-semibold text-text-sub dark:text-gray-400 uppercase tracking-widest">
            {team.length} Team Members
          </p>

          {/* Concentric Glass Floating Pill Toggle Switch */}
          <div className="flex p-1 rounded-full bg-white/50 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-sm backdrop-blur-md">
            <button
              onClick={() => setViewMode("grid")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${viewMode === "grid"
                  ? "bg-white dark:bg-[#162A45] text-primary dark:text-red-400 shadow-sm border border-black/[0.03] dark:border-white/5"
                  : "text-text-sub dark:text-gray-400 hover:text-text-main dark:hover:text-white"
                }`}
            >
              <Grid className="w-4 h-4" />
              <span>Grid</span>
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] ${viewMode === "list"
                  ? "bg-white dark:bg-[#162A45] text-primary dark:text-red-400 shadow-sm border border-black/[0.03] dark:border-white/5"
                  : "text-text-sub dark:text-gray-400 hover:text-text-main dark:hover:text-white"
                }`}
            >
              <List className="w-4 h-4" />
              <span>List</span>
            </button>
          </div>
        </div>

        {/* Directory Grid / List */}
        {isLoadingTeam ? (
          <div className="flex justify-center items-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary"></div>
          </div>
        ) : team.length > 0 ? (
          <div
            className={`grid gap-8 items-start ${viewMode === "grid"
                ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                : "grid-cols-1"
              }`}
          >
            {team.map((member, i) => (
              <div
                key={i}
                className={`reveal group relative p-1.5 rounded-[2.25rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-[0_20px_40px_-15px_rgba(12,27,46,0.03)] card-spring hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-20px_rgba(12,27,46,0.08)] hover:border-primary/20 ${viewMode === "list" ? "w-full" : "h-fit"
                  }`}
              >
                {/* Card Core container */}
                <div
                  className={`rounded-[1.85rem] bg-white dark:bg-canvas-dark border border-black/5 dark:border-white/5 overflow-hidden flex ${viewMode === "list"
                      ? "flex-col md:flex-row p-6 md:p-8 gap-8 items-center md:items-start"
                      : "flex-col"
                    }`}
                >
                  {/* Visual Avatar - Portrait nested image */}
                  <div
                    className={`relative overflow-hidden bg-canvas dark:bg-canvas-dark border border-black/5 dark:border-white/5 group shrink-0 ${viewMode === "list"
                        ? "w-full md:w-1/3 max-w-[240px] aspect-[4/5] rounded-[1.5rem] self-center md:self-start"
                        : "aspect-[4/5] rounded-[1.5rem] m-3"
                      }`}
                  >
                    <img
                      alt={member.name}
                      className="h-full w-full object-cover object-top transition-all duration-700 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:scale-103"
                      src={member.img}
                      loading="lazy"
                    />

                    {/* Social Overlay for Grid View (Slides up inside image frame) */}
                    {viewMode === "grid" && (
                      <div className="absolute inset-x-0 bottom-0 flex h-14 translate-y-full items-center justify-center bg-white/95 dark:bg-canvas-dark/95 backdrop-blur-md transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] group-hover:translate-y-0 gap-3.5 border-t border-black/5 dark:border-white/5 z-20">
                        {member.socials?.x && (
                          <a
                            href={member.socials.x}
                            target="_blank"
                            rel="noreferrer"
                            className="flex size-9 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 hover:bg-primary hover:text-white transition-all duration-300 hover:scale-110 text-text-main dark:text-white"
                            aria-label={`Visit ${member.name}'s X (Twitter) profile`}
                          >
                            <svg viewBox="0 0 24 24" className="w-4.5 h-4.5 fill-current">
                              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                            </svg>
                          </a>
                        )}
                        {member.socials?.instagram && (
                          <a
                            href={member.socials.instagram}
                            target="_blank"
                            rel="noreferrer"
                            className="flex size-9 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 hover:bg-primary hover:text-white transition-all duration-300 hover:scale-110 text-text-main dark:text-white"
                            aria-label={`Visit ${member.name}'s Instagram profile`}
                          >
                            <svg viewBox="0 0 24 24" className="w-4.5 h-4.5 fill-current">
                              <path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" />
                            </svg>
                          </a>
                        )}
                        {member.socials?.linkedin && (
                          <a
                            href={member.socials.linkedin}
                            target="_blank"
                            rel="noreferrer"
                            className="flex size-9 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 hover:bg-primary hover:text-white transition-all duration-300 hover:scale-110 text-text-main dark:text-white"
                            aria-label={`Visit ${member.name}'s LinkedIn profile`}
                          >
                            <svg viewBox="0 0 24 24" className="w-4.5 h-4.5 fill-current">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
                            </svg>
                          </a>
                        )}
                        {member.socials?.email && (
                          <a
                            href={member.socials.email}
                            className="flex size-9 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 hover:bg-primary hover:text-white transition-all duration-300 hover:scale-110 text-text-main dark:text-white"
                            aria-label={`Send an email to ${member.name}`}
                          >
                            <Mail className="w-4.5 h-4.5" />
                          </a>
                        )}
                        {member.socials?.github && (
                          <a
                            href={member.socials.github}
                            target="_blank"
                            rel="noreferrer"
                            className="flex size-9 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 hover:bg-primary hover:text-white transition-all duration-300 hover:scale-110 text-text-main dark:text-white"
                            aria-label={`Visit ${member.name}'s GitHub profile`}
                          >
                            <svg viewBox="0 0 24 24" className="w-4.5 h-4.5 fill-current">
                              <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"></path>
                            </svg>
                          </a>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Content Block */}
                  <div
                    className={`flex flex-col flex-grow ${viewMode === "list" ? "w-full md:w-2/3" : "w-full p-6 pt-1"
                      }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-3">
                      <div>
                        <h3 className="font-display text-xl font-extrabold text-text-main dark:text-white group-hover:text-primary transition-colors duration-500 leading-snug">
                          {member.name}
                        </h3>
                        <p className="text-[10px] font-mono font-bold tracking-[0.15em] text-primary dark:text-red-400 uppercase mt-1">
                          {member.role}
                        </p>
                      </div>

                      {/* Socials Inline for List View */}
                      {viewMode === "list" && (
                        <div className="flex gap-2 shrink-0 self-start">
                          {member.socials?.x && (
                            <a
                              href={member.socials.x}
                              target="_blank"
                              rel="noreferrer"
                              className="flex size-8 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 hover:bg-primary hover:text-white transition-all duration-300 hover:scale-105 text-text-main dark:text-white"
                              aria-label={`Visit ${member.name}'s X (Twitter) profile`}
                            >
                              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path>
                              </svg>
                            </a>
                          )}
                          {member.socials?.instagram && (
                            <a
                              href={member.socials.instagram}
                              target="_blank"
                              rel="noreferrer"
                              className="flex size-8 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 hover:bg-primary hover:text-white transition-all duration-300 hover:scale-105 text-text-main dark:text-white"
                              aria-label={`Visit ${member.name}'s Instagram profile`}
                            >
                              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                                <path d="M7.8,2H16.2C19.4,2 22,4.6 22,7.8V16.2A5.8,5.8 0 0,1 16.2,22H7.8C4.6,22 2,19.4 2,16.2V7.8A5.8,5.8 0 0,1 7.8,2M7.6,4A3.6,3.6 0 0,0 4,7.6V16.4C4,18.39 5.61,20 7.6,20H16.4A3.6,3.6 0 0,0 20,16.4V7.6C20,5.61 18.39,4 16.4,4H7.6M17.25,5.5A1.25,1.25 0 0,1 18.5,6.75A1.25,1.25 0 0,1 17.25,8A1.25,1.25 0 0,1 16,6.75A1.25,1.25 0 0,1 17.25,5.5M12,7A5,5 0 0,1 17,12A5,5 0 0,1 12,17A5,5 0 0,1 7,12A5,5 0 0,1 12,7M12,9A3,3 0 0,0 9,12A3,3 0 0,0 12,15A3,3 0 0,0 15,12A3,3 0 0,0 12,9Z" />
                              </svg>
                            </a>
                          )}
                          {member.socials?.linkedin && (
                            <a
                              href={member.socials.linkedin}
                              target="_blank"
                              rel="noreferrer"
                              className="flex size-8 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 hover:bg-primary hover:text-white transition-all duration-300 hover:scale-105 text-text-main dark:text-white"
                              aria-label={`Visit ${member.name}'s LinkedIn profile`}
                            >
                              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"></path>
                              </svg>
                            </a>
                          )}
                          {member.socials?.email && (
                            <a
                              href={member.socials.email}
                              className="flex size-8 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 hover:bg-primary hover:text-white transition-all duration-300 hover:scale-105 text-text-main dark:text-white"
                              aria-label={`Send an email to ${member.name}`}
                            >
                              <Mail className="w-4 h-4" />
                            </a>
                          )}
                          {member.socials?.github && (
                            <a
                              href={member.socials.github}
                              target="_blank"
                              rel="noreferrer"
                              className="flex size-8 items-center justify-center rounded-full bg-black/5 dark:bg-white/10 hover:bg-primary hover:text-white transition-all duration-300 hover:scale-105 text-text-main dark:text-white"
                              aria-label={`Visit ${member.name}'s GitHub profile`}
                            >
                              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current">
                                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"></path>
                              </svg>
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    <ShowMoreText text={member.intro} limit={viewMode === "list" ? 220 : 130} />

                    {/* Concentric tags container */}
                    <div
                      className={`flex flex-wrap gap-1.5 opacity-90 mt-auto ${viewMode === "list"
                          ? "pt-5 mt-6 border-t border-black/[0.04] dark:border-white/5"
                          : "pt-5"
                        }`}
                    >
                      {member.tags.map((tag: string) => (
                        <span
                          key={tag}
                          className="inline-flex items-center rounded-full bg-canvas dark:bg-white/5 px-2.5 py-0.5 text-[10px] font-medium text-text-sub dark:text-gray-400 border border-black/[0.03] dark:border-white/5"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-text-sub dark:text-gray-400">
            No team members have been added yet.
          </div>
        )}

        {/* Work With Us - Closing Band on soft inverted brand deep blue surface */}
        <div className="reveal mt-24 mb-16 p-1.5 rounded-[2.5rem] bg-white/40 dark:bg-white/5 border border-black/5 dark:border-white/5 shadow-lg w-full">
          <div className="relative rounded-[2.1rem] bg-gradient-to-br from-[#122234] to-[#0C1B2E] dark:from-[#081220] dark:to-[#0C1B2E] border border-black/5 dark:border-white/5 p-8 md:p-12 overflow-hidden text-center flex flex-col items-center">
            {/* Ambient radial brand-red accent blob inside card */}
            <div className="absolute inset-0 bg-[radial-gradient(600px_300px_at_50%_0%,rgba(238,44,40,0.14),transparent_70%)] pointer-events-none" />

            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-red-400 mb-4 block relative z-10">
              WORK WITH US
            </span>

            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-[-0.03em] text-white mb-4 max-w-xl relative z-10">
              Want to build the digital infrastructure of tomorrow?
            </h2>

            <p className="text-sm md:text-base text-gray-300 max-w-lg mb-8 leading-relaxed relative z-10">
              We are always looking for passionate cybersecurity experts, software engineers, and digital innovators to join our ranks.
            </p>

            {/* Button-in-Button Primary Pill CTA */}
            <button
              onClick={() => navigate("/contact")}
              className="group bg-white text-canvas-dark hover:bg-red-500 hover:text-white transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)] relative z-10 flex items-center gap-6 pl-6 pr-2 py-2 rounded-full font-bold text-xs uppercase tracking-wider shadow-md hover:shadow-xl hover:scale-98 active:scale-95"
            >
              <span>Start a Conversation</span>
              <span className="size-8 rounded-full bg-primary/10 group-hover:bg-white/20 flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.16,1.16,0.3,1)] group-hover:translate-x-1">
                <svg viewBox="0 0 12 12" className="w-3.5 h-3.5 stroke-current fill-none stroke-[2]">
                  <path d="M2.5 6h7M6 2.5L9.5 6 6 9.5" />
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DirectoryScreen;
